import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Bell, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { LoadingSpinner } from '@/components/loading-spinner'
import {
  useNotificationEventTypes,
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/lib/api'
import { useAuth } from '@/lib/auth-oidc'

export const Route = createFileRoute('/settings/notifications')({
  component: NotificationSettingsPage,
})

const GROUP_LABELS: Record<string, string> = {
  search: 'notificationSettings.group.search',
  rental: 'notificationSettings.group.rental',
  reminders: 'notificationSettings.group.reminders',
  snow_hunt: 'notificationSettings.group.snowHunt',
}

const GROUP_ORDER = ['search', 'rental', 'reminders', 'snow_hunt']

function NotificationSettingsPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()

  const { data: eventTypesData, isLoading: isTypesLoading } =
    useNotificationEventTypes()
  const { data: preferencesData, isLoading: isPrefsLoading } =
    useNotificationPreferences()
  const updateMutation = useUpdateNotificationPreferences()

  const isLoading = isTypesLoading || isPrefsLoading

  if (isAuthLoading) return <LoadingSpinner variant="fullscreen" />
  if (!isAuthenticated) return null

  const prefMap = new Map(
    (preferencesData?.preferences ?? []).map((p) => [p.event_type, p.enabled]),
  )

  const groupedTypes = GROUP_ORDER.map((group) => ({
    group,
    label: t(GROUP_LABELS[group] ?? group),
    types: (eventTypesData?.event_types ?? []).filter(
      (et) => et.group === group,
    ),
  })).filter((g) => g.types.length > 0)

  const handleToggle = async (eventType: string) => {
    const current = prefMap.get(eventType) ?? true
    try {
      await updateMutation.mutateAsync({ [eventType]: !current })
    } catch {
      toast.error(t('common.error'))
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b safe-area-top">
        <div className="container mx-auto max-w-screen-md px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: '/settings' })}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">
            {t('notificationSettings.title')}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {isLoading && <LoadingSpinner />}

          {!isLoading &&
            groupedTypes.map(({ group, label, types }) => (
              <Card key={group}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{label}</CardTitle>
                  <CardDescription>
                    {t('notificationSettings.groupDescription')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {types.map((eventType) => {
                    const enabled =
                      prefMap.get(eventType.event_type) ??
                      eventType.default_enabled
                    return (
                      <div
                        key={eventType.event_type}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm">
                          {eventType.description}
                        </span>
                        <Switch
                          checked={enabled}
                          onCheckedChange={() =>
                            handleToggle(eventType.event_type)
                          }
                          disabled={updateMutation.isPending}
                        />
                      </div>
                    )
                  })}
                </CardContent>
              </Card>
            ))}

          {!isLoading && groupedTypes.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {t('notificationSettings.empty')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
